import {
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
} from "react"
import { useAnimationTuning } from "../../../animation/animationTuningContext"
import { resolveJsEasing } from "../../../animation/animationTuningDefaults"
import { easeInOutCubic } from "../../../extrapolated-graph/hooks"
import { lerpNumber } from "../interpolate"
import { PLAN_PREVIEW_FADE_MS } from "../plan-fan/planPreviewFan"

// The strip's frame is a pure function of date, so unlike the desktop
// graph (built-in viewport tween) camera steps need their own smoothing.
// These hooks run a requestAnimationFrame loop ONLY while a tween is
// mid-flight — e.g. the editor's scrubbed preview doesn't re-render per
// frame the way the production strip (animated date) does.

type Tween = { from: number; to: number; startedAt: number }

const makeTween = (from: number, to: number): Tween | null =>
  from === to ? null : { from, to, startedAt: performance.now() }

// Re-renders once per animation frame while a tween runs.
const useTweenFrameTicker = (
  tweenRef: MutableRefObject<Tween | null>,
  restartKey: unknown,
) => {
  const [, setTweenClock] = useState(0)
  useEffect(() => {
    if (tweenRef.current === null) return
    let frameId = requestAnimationFrame(function tick(nowMs: number) {
      setTweenClock(nowMs)
      if (tweenRef.current !== null) frameId = requestAnimationFrame(tick)
    })
    return () => cancelAnimationFrame(frameId)
  }, [tweenRef, restartKey])
}

// The tween's current value; clears it and returns `settled` once done.
const sampleTween = <S extends number | undefined>(
  tweenRef: MutableRefObject<Tween | null>,
  displayedRef: { current: number | undefined },
  durationMs: number,
  ease: (t: number) => number,
  settled: S,
): number | S => {
  const tween = tweenRef.current
  if (tween !== null) {
    const progress = (performance.now() - tween.startedAt) / durationMs
    if (progress < 1) {
      const value = lerpNumber(tween.from, tween.to, ease(progress))
      displayedRef.current = value
      return value
    }
    tweenRef.current = null
  }
  displayedRef.current = settled
  return settled
}

// Smooths one keyframed camera channel. `target` is the inherited
// keyframe value at the story date — a step function that jumps at set
// markers; `fallback` is the automatic camera's value for the same
// channel. Steps ease over the tuned mobileZoomTweenMs: into the first
// keyframe from the automatic value, between keyframe values, and back
// out toward the automatic value when the date rewinds past the first
// set marker. Returns undefined — automatic camera in charge — whenever
// no tween is in flight and no keyframe covers the date.
export const useTweenedZoomChannel = (
  target: number | undefined,
  fallback: number,
): number | undefined => {
  const tuning = useAnimationTuning()
  const displayedRef = useRef<number | undefined>(target)
  const tweenRef = useRef<Tween | null>(null)
  const lastTargetRef = useRef<number | undefined>(target)

  if (target !== lastTargetRef.current) {
    // Ease from whatever is displayed (mid-tween retargets included);
    // entering/leaving keyframe coverage eases via the automatic value.
    tweenRef.current = makeTween(displayedRef.current ?? fallback, target ?? fallback)
    lastTargetRef.current = target
  }
  useTweenFrameTicker(tweenRef, target)

  return sampleTween(
    tweenRef,
    displayedRef,
    Math.max(1, tuning.mobileZoomTweenMs),
    resolveJsEasing(tuning.mobileZoomTweenEasing),
    target,
  )
}

// Eases a 0→1 blend toward `active` over PLAN_PREVIEW_FADE_MS — the same
// duration the choice-point fan fades over — so the camera glide from
// the pre-choice close-up into the fan frame plays in lockstep with the
// fan appearing: the main line slides into place WHILE the other four
// lines fade in, not a hard snap.
export const useChoicePointBlend = (active: boolean): number => {
  const displayedRef = useRef(active ? 1 : 0)
  const tweenRef = useRef<Tween | null>(null)
  const lastActiveRef = useRef(active)

  if (active !== lastActiveRef.current) {
    tweenRef.current = makeTween(displayedRef.current, active ? 1 : 0)
    lastActiveRef.current = active
  }
  useTweenFrameTicker(tweenRef, active)

  return sampleTween(
    tweenRef,
    displayedRef,
    PLAN_PREVIEW_FADE_MS,
    easeInOutCubic,
    active ? 1 : 0,
  )
}
